import { CanvasToggleButton } from '@mygpt/app/chat/components/Canvas/ToggleButton';
import { TTSButton } from './TTSButton';
import { CopyButton } from './CopyButton';
import { TransferToHumanButton } from './TransferToHumanButton';
import { ChatContentBottomButtonsProps } from '../ChatContentBottomButton';
import { useBot } from '@mygpt/app/bot';
import { useMemo } from 'react';
import { DislikeButton, LikeButton } from '@mygpt/app/chat/components/FeedbackButton';
import { ratingType } from '@mygpt/types';
import { cleanAgentContent } from '@mygpt/app/utils/agent';

export const MobileView = ({
  item,
  id,
  sessionId,
  pageSource,
  enableTTS,
  isPlaying,
  ttsLoading,
  onTTSPlayOrPause,
  copied,
  setCopied,
}: ChatContentBottomButtonsProps) => {
  const { robotInfo } = useBot();

  const configs = useMemo(() => {
    return robotInfo?.configs;
  }, [robotInfo]);

  // 计算可见按钮
  const visibleButtons = {
    tts: enableTTS,
    like: true,
    dislike: true,
    copy: true,
    // 统一转人工按钮的判断逻辑，与 TransferToHuman 组件保持一致
    transferToHuman: !!configs?.feedback_form?.content || !!configs?.feedback_form?.forward_chat_history,
    canvas: !!item.canvas_url,
  };

  // 计算除Canvas外的其他按钮数量
  const otherButtonsCount = Object.entries(visibleButtons).filter(
    ([key, visible]) => visible && key !== 'canvas'
  ).length;

  // 收集所有非Canvas按钮
  const otherButtons: React.ReactNode[] = [];

  if (visibleButtons.tts) {
    otherButtons.push(
      <TTSButton
        enableTTS={enableTTS}
        text={item.text}
        isPlaying={isPlaying}
        ttsLoading={ttsLoading}
        onTTSPlayOrPause={onTTSPlayOrPause}
      />
    );
  }

  if (visibleButtons.like) {
    otherButtons.push(
      <LikeButton
        aiId={id}
        sessionId={sessionId}
        messageId={item.messageId || ''}
        rating={item.rating as ratingType}
        pageSource={pageSource}
      />
    );
  }

  if (visibleButtons.dislike) {
    otherButtons.push(
      <DislikeButton
        aiId={id}
        sessionId={sessionId}
        messageId={item.messageId || ''}
        rating={item.rating as ratingType}
        pageSource={pageSource}
      />
    );
  }

  if (visibleButtons.copy) {
    // 清理 Agent 特殊标签，只保留 ANSWER 内容用于复制
    const cleanedText = cleanAgentContent(item.text);
    otherButtons.push(<CopyButton key="copy" text={cleanedText} copied={copied} setCopied={setCopied} />);
  }

  if (visibleButtons.transferToHuman) {
    otherButtons.push(<TransferToHumanButton messageId={item.messageId || ''} />);
  }

  // 渲染布局的主要逻辑
  const renderLayout = () => {
    if (visibleButtons.canvas) {
      // 有Canvas的情况
      if (otherButtonsCount === 5) {
        // 情况1: Canvas + 5个其他按钮 (6个按钮，两行3+3)
        return (
          <div className="mt-2 space-y-2">
            {/* 第一行：前3个按钮 */}
            <div className="flex gap-2">{otherButtons.slice(0, 3)}</div>

            {/* 第二行：后2个按钮 + Canvas */}
            <div className="flex gap-2">
              {otherButtons.slice(3)}
              <CanvasToggleButton
                messageId={item.messageId || ''}
                canvas_url={item.canvas_url || ''}
                canvas_title={item.canvas_title || ''}
                isMobile={true}
                compact={true}
              />
            </div>
          </div>
        );
      } else {
        // 情况2: Canvas + 少于5个其他按钮 (其他按钮一行，Canvas独占一行)
        return (
          <div className="mt-2 space-y-2">
            {/* 其他按钮行 */}
            {otherButtons.length > 0 && <div className="flex gap-2">{otherButtons}</div>}

            {/* Canvas独占一行 */}
            <CanvasToggleButton
              messageId={item.messageId || ''}
              canvas_url={item.canvas_url || ''}
              canvas_title={item.canvas_title || ''}
              isMobile={true}
              compact={false}
            />
          </div>
        );
      }
    } else {
      // 没有Canvas的情况
      if (otherButtonsCount <= 4) {
        // 情况3: ≤4个按钮，一行展示
        return (
          <div className="mt-2">
            <div className="flex gap-2">{otherButtons}</div>
          </div>
        );
      } else if (otherButtonsCount === 5) {
        // 情况4: 5个按钮，两行3+2
        return (
          <div className="mt-2 space-y-2">
            {/* 第一行：前3个按钮 */}
            <div className="flex gap-2">{otherButtons.slice(0, 3)}</div>

            {/* 第二行：后2个按钮，居中展示 */}
            <div className="flex gap-2 justify-center">{otherButtons.slice(3)}</div>
          </div>
        );
      }
    }

    // 兜底情况，如果没有任何按钮
    return null;
  };

  return renderLayout();
};
